import { isLoggedIn, LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";

import {
  projectViewLinksQueryKey,
  type ProjectViewLinksResponseData,
} from "../pages/ViewLinkListPage/ViewLinkListPage";
import { SyncRequestClient } from "../stores/requestClient";
import { SearchParam } from "../types";
import { ServiceErrorCode } from "../utils/httpUtils";
import { type Brand, buildTypePredicateFilter } from "../utils/typeUtils";
import type { UserID } from "./useAccount";
import { type ProjectData, projectQueryKey } from "./useProject";
import {
  useWebExport,
  type WebExportBaseRole,
  type WebExportData,
  webExportQueryKey,
  type WebExportSharedState,
} from "./useWebExport";

type UpdateProjectViewLinkRequestPayload = {
  sharedState: WebExportSharedState;
  baseRole?: WebExportBaseRole;
};

export const projectViewLinkSharingQueryKey = ["projectViewLinkSharingQueryKey"];

export const useMutateProjectViewLink = ({
  onSuccess,
  onError,
  teamID,
}: {
  onSuccess?: (variables: UpdateProjectViewLinkRequestPayload) => void;
  onError?: (error: Error, variables: UpdateProjectViewLinkRequestPayload) => void;
  teamID?: string;
} = {}) => {
  const queryClient = useQueryClient();
  const { projectId } = useParams();

  return useMutation({
    mutationFn: async ({
      viewLinkId,
      body,
    }: {
      viewLinkId: string;
      body: UpdateProjectViewLinkRequestPayload;
    }) => {
      await SyncRequestClient.authenticatedRequest({
        method: "PATCH",
        url: `/web/project-view-links/${viewLinkId}`,
        body,
      });
    },
    onSuccess: (_, variables) => {
      const { sharedState, baseRole } = variables.body;
      queryClient.setQueriesData<ProjectData | undefined>(
        { queryKey: [...projectQueryKey, projectId] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as ProjectData;
          deepCopy.projectViewLinks = deepCopy.projectViewLinks?.filter(
            (item) => item.projectViewLinkId != variables.viewLinkId
          );
          return {
            ...deepCopy,
          };
        }
      );

      queryClient.setQueriesData<WebExportData | undefined>(
        { queryKey: [...webExportQueryKey] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData));
          return {
            ...deepCopy,
            sharedState,
            baseRole,
          };
        }
      );

      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        { queryKey: [...projectViewLinkSharingQueryKey, variables.viewLinkId] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData));
          return {
            ...deepCopy,
            sharedState,
            baseRole,
          };
        }
      );

      if (teamID) {
        queryClient.setQueriesData<ProjectViewLinksResponseData | undefined>(
          { queryKey: [...projectViewLinksQueryKey, teamID] },
          (oldData) => {
            if (!oldData) {
              return oldData;
            }

            const deepCopy = JSON.parse(JSON.stringify(oldData));
            deepCopy.projectViewLinks = oldData.projectViewLinks.filter(
              ({ projectViewLinkId }) => projectViewLinkId !== variables.viewLinkId
            );
            return deepCopy;
          }
        );
      }

      onSuccess?.(variables.body);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to update project viewlink", error });
      onError?.(error, variables.body);
    },
  });
};

type ProjectViewLinkUserBase = {
  email: string;
  baseRole: WebExportBaseRole;
};

export type ProjectViewLinkMember = ProjectViewLinkUserBase & {
  userId: UserID;
  name?: string;
};

export type InvitationID = Brand<string, "InvitationID">;

export type ProjectViewLinkInvitation = ProjectViewLinkUserBase & {
  invitationId: InvitationID;
};

export type ProjectViewLinkUser = ProjectViewLinkMember | ProjectViewLinkInvitation;

export type SpaceID = Brand<string, "SpaceID">;

export const ProjectViewLinkSpaceTypes = {
  Private: "private",
  Team: "team",
} as const;

export type ProjectViewLinkSharingResponseData = {
  users: ProjectViewLinkUser[];
  baseRole: WebExportBaseRole;
  sharedState: WebExportSharedState;
  space: {
    spaceId: SpaceID;
    spaceName?: string;
    spaceType: (typeof ProjectViewLinkSpaceTypes)[keyof typeof ProjectViewLinkSpaceTypes];
    spaceUsers?: {
      userId: UserID;
      name?: string;
      email: string;
    }[];
  };
};

export const isProjectViewLinkInvitation = buildTypePredicateFilter<
  ProjectViewLinkUser,
  ProjectViewLinkInvitation
>("invitationId");

export const isProjectViewLinkMember = buildTypePredicateFilter<
  ProjectViewLinkUser,
  ProjectViewLinkMember
>("userId");

export const useProjectViewLinkSharing = () => {
  const { viewLink } = useParams();
  const { data: WebExportData } = useWebExport();

  return useQuery({
    queryKey: [...projectViewLinkSharingQueryKey, viewLink],
    queryFn: async () => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "GET",
        url: `/web/project-view-links/${viewLink}/sharing`,
      });

      return response.json<ProjectViewLinkSharingResponseData>();
    },
    select: (data) => {
      const members: ProjectViewLinkMember[] = data.users.filter(isProjectViewLinkMember);
      const invitations: ProjectViewLinkInvitation[] = data.users.filter(
        isProjectViewLinkInvitation
      );

      return { ...data, members, invitations };
    },
    enabled: isLoggedIn() && Boolean(WebExportData?.userRole === "owner"),
  });
};

export type ProjectViewLinkGetInvitationResponseData = {
  email?: string;
  action: "view" | "accept" | "signup" | "login";
};

export const projectViewLinkInvitationQueryKey = ["projectViewLinkInvitationQueryKey"];

export const useProjectViewLinkInvitation = () => {
  const { viewLink } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  const enabled = Boolean(invitationHash);

  const query = useQuery({
    queryKey: [...projectViewLinkInvitationQueryKey],
    queryFn: async () => {
      const request = isLoggedIn()
        ? SyncRequestClient.authenticatedRequest
        : SyncRequestClient.publicRequest;

      const response = await request({
        method: "GET",
        url: `/web/project-view-links/${viewLink}/invitations-by-hash/${invitationHash}`,
      });

      return response.json<ProjectViewLinkGetInvitationResponseData>();
    },
    enabled,
  });

  return { ...query, enabled };
};

export type CreateProjectViewLinkInvitationRequestPayload = {
  emails: string[];
  baseRole: WebExportBaseRole;
};

export type CreateProjectViewLinkInvitationResponseData = {
  invitedUsers: ProjectViewLinkInvitation[];
  errors: {
    email: string;
    message: string;
    code: string;
  }[];
};

export const useMutateCreateProjectViewLinkInvitation = ({
  onSuccess,
  onError,
}: {
  onSuccess?: (variables: CreateProjectViewLinkInvitationRequestPayload) => void;
  onError?: (error: Error, variables: CreateProjectViewLinkInvitationRequestPayload) => void;
} = {}) => {
  const { viewLink } = useParams();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: CreateProjectViewLinkInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "POST",
        url: `/web/project-view-links/${viewLink}/invitations`,
        body,
      });

      return response.json<CreateProjectViewLinkInvitationResponseData>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        { queryKey: [...projectViewLinkSharingQueryKey, viewLink] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as typeof oldData;
          const invitations = oldData.users.filter(isProjectViewLinkInvitation);
          const members = oldData.users.filter(isProjectViewLinkMember);

          const mappedUsers = [...members, ...data.invitedUsers, ...invitations];

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      onSuccess?.(variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to create invitations", error });
      onError?.(error, variables);
    },
  });
};

export const useMutateAcceptInvitation = () => {
  const { viewLink } = useParams();
  const [searchParams] = useSearchParams();
  const invitationHash = searchParams.get(SearchParam.InvitationHash) ?? "";

  return useMutation({
    mutationFn: async ({ emailToken }: { emailToken?: string | null } = {}) => {
      try {
        const response = await SyncRequestClient.authenticatedRequest({
          method: "POST",
          url: `/web/project-view-links/${viewLink}/invitations-by-hash/${invitationHash}/accept`,
          body: { ...(emailToken && { emailToken }) },
        });
        return response;
      } catch (error) {
        // Accept invitation is best effort.
        // Not found could mean you can view the viewlink or could mean you can't.
        // Up to the viewlink loading to decide and show the proper error screen.
        if (
          error instanceof ServiceError &&
          (error.hasCode(ServiceErrorCode.NotFound) ||
            error.hasCode(ServiceErrorCode.InvitationNotFound))
        ) {
          return;
        }
        throw error;
      }
    },
  });
};

export type UpdateProjectViewLinkUserRequestPayload = {
  baseRole: WebExportBaseRole;
};

export const useMutateUpdateProjectViewLinkUser = ({
  userId,
  onSuccess,
  onError,
}: {
  userId: UserID;
  onSuccess?: (variables: UpdateProjectViewLinkUserRequestPayload) => void;
  onError?: (error: Error, variables: UpdateProjectViewLinkUserRequestPayload) => void;
}) => {
  const { viewLink } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateProjectViewLinkUserRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/project-view-links/${viewLink}/users/${userId}`,
        body,
      });

      return response.json<ProjectViewLinkMember>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        { queryKey: [...projectViewLinkSharingQueryKey, viewLink] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as typeof oldData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectViewLinkMember(user) && user.userId === userId) {
              return {
                ...data,
              };
            }

            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      onSuccess?.(variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to update project viewlink user", error });
      onError?.(error, variables);
    },
  });
};

export type UpdateProjectViewLinkInvitationRequestPayload = {
  baseRole: WebExportBaseRole;
};

export const useMutateUpdateProjectViewLinkInvitation = ({
  invitationId,
  onSuccess,
  onError,
}: {
  invitationId: InvitationID;
  onSuccess?: (variables: UpdateProjectViewLinkInvitationRequestPayload) => void;
  onError?: (error: Error, variables: UpdateProjectViewLinkInvitationRequestPayload) => void;
}) => {
  const { viewLink } = useParams();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (body: UpdateProjectViewLinkInvitationRequestPayload) => {
      const response = await SyncRequestClient.authenticatedRequest({
        method: "PUT",
        url: `/web/project-view-links/${viewLink}/invitations/${invitationId}`,
        body,
      });

      return response.json<ProjectViewLinkInvitation>();
    },
    onSuccess: (data, variables) => {
      queryClient.setQueriesData<ProjectViewLinkSharingResponseData | undefined>(
        { queryKey: [...projectViewLinkSharingQueryKey, viewLink] },
        (oldData) => {
          if (!oldData) {
            return oldData;
          }

          const deepCopy = JSON.parse(JSON.stringify(oldData)) as typeof oldData;
          const mappedUsers = deepCopy.users.map((user) => {
            if (isProjectViewLinkInvitation(user) && user.invitationId === invitationId) {
              return {
                ...data,
              };
            }

            return user;
          });

          return {
            ...deepCopy,
            users: mappedUsers,
          };
        }
      );

      onSuccess?.(variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to update project viewlink invitation", error });
      onError?.(error, variables);
    },
  });
};

export const useMutateDeleteProjectViewLinkUser = ({
  userId,
  onSuccess,
  onError,
}: {
  userId: UserID;
  onSuccess?: (data: void, variables: void) => void;
  onError?: (error: Error, variables: void) => void;
}) => {
  const { viewLink } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/project-view-links/${viewLink}/users/${userId}`,
      });
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to delete project viewlink user", error });
      onError?.(error, variables);
    },
  });

  const removeUserFromCache = () => {
    queryClient.setQueryData<ProjectViewLinkSharingResponseData | undefined>(
      [...projectViewLinkSharingQueryKey, viewLink],
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) => !(isProjectViewLinkMember(user) && user.userId === userId)
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeUserFromCache,
  };
};

export const useMutateDeleteProjectViewLinkInvitation = ({
  invitationId,
  onSuccess,
  onError,
}: {
  invitationId: InvitationID;
  onSuccess?: (data: void, variables: void) => void;
  onError?: (error: Error, variables: void) => void;
}) => {
  const { viewLink } = useParams();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async () => {
      await SyncRequestClient.authenticatedRequest({
        method: "DELETE",
        url: `/web/project-view-links/${viewLink}/invitations/${invitationId}`,
      });
    },
    onSuccess: (data, variables) => {
      onSuccess?.(data, variables);
    },
    onError: (error, variables) => {
      LogSentryException({ messagePrefix: "Failed to delete project viewlink invitation", error });
      onError?.(error, variables);
    },
  });

  const removeInvitationFromCache = () => {
    queryClient.setQueryData<ProjectViewLinkSharingResponseData | undefined>(
      [...projectViewLinkSharingQueryKey, viewLink],
      (oldData) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          users: oldData.users.filter(
            (user) => !(isProjectViewLinkInvitation(user) && user.invitationId === invitationId)
          ),
        };
      }
    );
  };

  return {
    ...mutation,
    removeInvitationFromCache,
  };
};
